import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ShoppingBag, MapPin, User, Phone, CheckCircle, Calendar, Clock } from 'lucide-react';

import { useRouteId } from '../lib/routeHelper';
import { getAvailableTimeSlots } from '../lib/date';
import { useCurrencySymbol } from '../lib/currency';
import { Header } from '../components/Header';
import { useCartStore } from '../stores/useCartStore';
import { useAuthStore } from '../stores/useAuthStore';
import {
    deliverySchema,
    pickupSchema,
    type DeliveryFormValues,
    type PickupFormValues
} from '../schemas/order.schema';

import { AddressList } from '../components/profile/AddressList';
import { AddressModal } from '../components/profile/AddressModal';
import { useProfile, useGoogleLogin } from '../hooks/queries/useAuth';
import { useAddresses } from '../hooks/queries/useAddresses';
import type { Address } from '../types';
import { useCreateBatchOrders, useCreateOrder } from '../hooks/queries/useOrders';
import { GoogleLogin } from '@react-oauth/google';
import { useQuoteMutation } from '../hooks/queries/useShipments';
import type { QuoteResponse } from '../services/shipment.service';

// Union type for the form
type OrderFormValues = (DeliveryFormValues & PickupFormValues) & {
    contact_email?: string;
    scheduled_times?: Record<string, string>;
    address?: {
        street: string;
        number: string;
        floor?: string;
        apartment?: string;
        district: string;
        reference?: string;
        latitude?: number;
        longitude?: number;
    }
};

export function OrderConfirmationPage() {
    const { tableId } = useParams<{ tableId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const currencySymbol = useCurrencySymbol();
    const {
        items,
        offers,
        notes,
        getTotal,
        scheduledOrders,
        hasScheduledOrders,
        hasImmediateOrders,
        getTotalByDate
    } = useCartStore();

    const { isTakeAway, identifier, sessionId, orderMode: storedOrderMode, isAuthenticated, loginWithGoogle, config } = useAuthStore();
    const { data: profile } = useProfile();

    // Queries
    const createOrderMutation = useCreateOrder();
    const createBatchOrdersMutation = useCreateBatchOrders();
    const loginMutation = useGoogleLogin();

    const routeId = useRouteId();
    const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
    const [addressToEdit, setAddressToEdit] = useState<Address | undefined>(undefined);

    const quoteMutation = useQuoteMutation();
    const [deliveryQuote, setDeliveryQuote] = useState<QuoteResponse | null>(null);
    const [isGettingQuote, setIsGettingQuote] = useState(false);
    const [quoteError, setQuoteError] = useState<string | null>(null);

    const isEmail = identifier && identifier.includes('@');
    const isPhone = identifier && !identifier.includes('@');

    // Determine order type
    const orderType = !isTakeAway
        ? 'dine_in'
        : (location.state?.orderType || storedOrderMode || 'pickup');

    // Schema selection based on order type
    let schema: any = orderType === 'delivery' ? deliverySchema : (orderType === 'pickup' ? pickupSchema : undefined);

    if (schema && hasScheduledOrders()) {
        schema = schema.extend({
            contact_email: z.string().min(1, 'Email requerido').email('Email inválido'),
            scheduled_times: z.record(z.string(), z.string()).optional()
        });
    }

    const { register, handleSubmit, setValue, control, formState: { errors } } = useForm<OrderFormValues>({
        resolver: schema ? zodResolver(schema) as any : undefined,
        defaultValues: {
            recipient_name: '',
            recipient_phone: isPhone ? identifier || '' : '',
            pickup_name: '',
            pickup_phone: isPhone ? identifier || '' : '',
            contact_email: isEmail ? identifier || '' : '',
            scheduled_times: {},
            address: {
                street: '',
                number: '',
                floor: '',
                apartment: '',
                district: '',
                reference: '',
                latitude: 0,
                longitude: 0
            }
        }
    });

    // Pre-fill form if profile loads later
    useEffect(() => {
        if (profile) {
            if (orderType === 'delivery') {
                setValue('recipient_name', profile.name || '');
                setValue('recipient_phone', profile.phone_number || '');
            } else if (orderType === 'pickup') {
                setValue('pickup_name', profile.name || '');
                setValue('pickup_phone', profile.phone_number || '');
            }
            if (profile.email) {
                setValue('contact_email', profile.email);
            }
        }
    }, [profile, orderType, setValue]);

    const total = getTotal();
    const grandTotal = total + scheduledOrders.reduce((sum, order) => sum + getTotalByDate(order.scheduledDate), 0);

    // Proper watchers at top level to avoid conditional hook rules violation
    const selectedAddressUuid = useWatch({ control, name: 'address_uuid' as any });
    const watchedAddress = useWatch({ control, name: 'address' as any });

    // Auto-select if only one address is available
    const { data: addresses } = useAddresses();

    // DEBUG: Verificar qué direcciones y coordenadas devuelve el backend
    useEffect(() => {
        if (addresses) {
            console.log('[DEBUG BACKEND ADDRESSES] Direcciones recibidas:', JSON.stringify(addresses));
        }
    }, [addresses]);

    useEffect(() => {
        if (isAuthenticated && addresses?.length === 1 && !selectedAddressUuid && orderType === 'delivery') {
            handleSelectAddress(addresses[0]);
        }
    }, [addresses, isAuthenticated, selectedAddressUuid, orderType]);

    // Request quote when delivery address is selected
    useEffect(() => {
        const fetchQuote = async () => {
            if (orderType === 'delivery' && watchedAddress?.street && watchedAddress?.number && profile) {
                const addressKey = `${watchedAddress.street}-${watchedAddress.number}-${watchedAddress.district}-${(watchedAddress as any).latitude}-${(watchedAddress as any).longitude}`;
                const cached = sessionStorage.getItem(`delivery_quote_${sessionId}`);

                setQuoteError(null);

                if (cached) {
                    const parsed = JSON.parse(cached);
                    if (parsed.addressKey === addressKey) {
                        setDeliveryQuote(parsed.quote);
                        return;
                    }
                }

                setIsGettingQuote(true);
                setQuoteError(null);
                try {
                    const quotePayload = {
                        destination_location: {
                            address: `${watchedAddress.street} ${watchedAddress.number}`,
                            city: watchedAddress.district || 'CABA',
                            contact_name: profile.name || 'Cliente',
                            contact_phone: profile.phone_number || identifier || '',
                            latitude: watchedAddress.latitude || 0,
                            longitude: watchedAddress.longitude || 0,
                        },
                        order_uuid: null
                    };
                    console.log('[DEBUG QUOTE] Enviando cotización:', JSON.stringify(quotePayload));
                    const quote = await quoteMutation.mutateAsync(quotePayload);
                    setDeliveryQuote(quote);
                    sessionStorage.setItem(`delivery_quote_${sessionId}`, JSON.stringify({
                        addressKey,
                        quote
                    }));
                } catch (error: any) {
                    console.error('Error fetching quote:', error);
                    setDeliveryQuote(null);
                    if (error?.message?.includes('No hay repartidores disponibles.')) {
                        setQuoteError('No hay repartidores disponibles.');
                    } else if (error?.message?.includes('No hay repartidores disponibles')) {
                        setQuoteError('No hay repartidores disponibles.');
                    } else {
                        setQuoteError('Error al calcular el envío.');
                    }
                } finally {
                    setIsGettingQuote(false);
                }
            } else {
                setDeliveryQuote(null);
                setQuoteError(null);
            }
        };

        fetchQuote();
    }, [watchedAddress?.street, watchedAddress?.number, (watchedAddress as any)?.latitude, (watchedAddress as any)?.longitude, orderType, profile, identifier, sessionId]);

    // Synchronize form with selected address data (in case of edits in modal)
    useEffect(() => {
        if (selectedAddressUuid && addresses) {
            const current = addresses.find(a => a.uuid === (selectedAddressUuid as any));
            if (current) {
                setValue('address.street', current.street);
                setValue('address.number', current.number);
                setValue('address.floor', current.floor || '');
                setValue('address.apartment', current.apartment || '');
                setValue('address.district', current.district || '');
                setValue('address.reference', current.reference || '');
                setValue('address.latitude', current.latitude || 0);
                setValue('address.longitude', current.longitude || 0);
            }
        }
    }, [addresses, selectedAddressUuid, setValue]);

    useEffect(() => {
        if (!hasImmediateOrders() && !hasScheduledOrders()) {
            navigate(`/menu/${tableId}`);
        }
    }, [hasImmediateOrders, hasScheduledOrders, navigate, tableId]);

    const formatDate = (dateString: string) => {
        const [year, month, day] = dateString.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        return date.toLocaleDateString('es-AR', {
            weekday: 'long',
            month: 'long',
            day: 'numeric'
        });
    };

    const handleSelectAddress = (address: Address) => {
        console.log('[DEBUG SELECT ADDRESS] Seleccionando:', JSON.stringify(address));
        // First check if address has street/number to avoid blank state
        if (!address.street || !address.number) {
            console.warn('Address missing street or number', address);
        }

        setValue('address_uuid', address.uuid);
        setValue('address.street', address.street);
        setValue('address.number', address.number);
        setValue('address.floor', address.floor || '');
        setValue('address.apartment', address.apartment || '');
        setValue('address.district', address.district || '');
        setValue('address.reference', address.reference || '');
        setValue('address.latitude', address.latitude || 0);
        setValue('address.longitude', address.longitude || 0);
    };

    const handleGoogleLogin = async (credentialResponse: any) => {
        if (credentialResponse.credential) {
            try {
                await loginMutation.mutateAsync(credentialResponse.credential);
                // Auth store update is handled in useGoogleLogin success? 
                // We probably need to update local auth store too.
                // The original code in useAuthStore sets state. 
                // Our hook calls service, but creating a wrapper that calls store is better.
                // Or we assume `loginWithGoogle` from store is used.
                // Let's use the store action directly if possible or ensure hook interacts with store.
                // Reverting to store action for simplicity of state update
                await loginWithGoogle(credentialResponse.credential);
            } catch (error) {
                console.error("Login failed", error);
                alert("Error al iniciar sesión con Google");
            }
        }
    };

    const onSubmit = async (data: OrderFormValues) => {
        // Manual validation for scheduled orders extra logic
        if (hasScheduledOrders()) {
            if (!data.contact_email?.includes('@')) {
                alert('Email requerido para pedidos programados');
                return;
            }
            // Validate times...
            for (const order of scheduledOrders) {
                // @ts-ignore
                if (!data.scheduled_times?.[order.scheduledDate]) {
                    alert('Por favor seleccioná un horario para cada pedido programado.');
                    return;
                }
            }
        }

        // Check if delivery requires address
        if (orderType === 'delivery') {
            if (!data.address?.street || !data.address?.number || !data.address?.district) {
                alert('Por favor complete la dirección de envío');
                return;
            }
        }

        try {
            const ordersToSubmit = [];

            // Common data construction
            const getExtraData = (isScheduled = false, scheduledTime?: string) => {
                const baseData: any = {};

                if (orderType === 'pickup') {
                    baseData.order_type = "takeaway";
                    baseData.pickup_name = data.pickup_name;
                    baseData.pickup_phone = data.pickup_phone;
                    if (isScheduled && scheduledTime) {
                        baseData.pickup_time = scheduledTime;
                    }
                    if (notes) {
                        baseData.notes = notes;
                    }
                } else if (orderType === 'delivery') {
                    baseData.order_type = "delivery";
                    baseData.address = `${data.address?.street} ${data.address?.number}`;
                    baseData.city = data.address?.district || 'CABA';
                    baseData.phone = data.recipient_phone;
                    baseData.recipient_name = data.recipient_name;
                    baseData.recipient_phone = data.recipient_phone;
                }

                // Add email for scheduled orders
                if (isScheduled && data.contact_email) {
                    baseData.contact_email = data.contact_email;
                }

                return {
                    delivery_quote_id: orderType === 'delivery' ? deliveryQuote?.uuid : undefined,
                    delivery_price: orderType === 'delivery' ? deliveryQuote?.price : undefined,
                    order_data: baseData
                };
            };

            const extraData = getExtraData();

            // Add immediate order if exists
            if (hasImmediateOrders()) {
                const immediateOrder: any = {
                    table: isTakeAway ? null : tableId,
                    session: sessionId || undefined,
                    client_identifier: isTakeAway ? sessionId || identifier : identifier,
                    order_products: items.map(item => ({
                        product: item.product.uuid,
                        quantity: item.quantity,
                        observation: item.observation,
                        sub_products: item.sub_products?.map(s => s.sub_product_uuid) || []
                    })),
                    order_offers: offers.map(item => ({
                        offer: item.offer.uuid,
                        quantity: item.quantity,
                        observation: item.observation
                    })),
                    observation: notes || undefined,
                    ...extraData,
                    latitude: data.address?.latitude,
                    longitude: data.address?.longitude,
                    idempotency_key: `order-${sessionId}-${Date.now()}`
                };
                ordersToSubmit.push(immediateOrder);
            }

            // Add scheduled orders
            for (const scheduledOrder of scheduledOrders) {
                // @ts-ignore
                const selectedTime = data.scheduled_times?.[scheduledOrder.scheduledDate] || '12:00';
                const scheduledExtraData = getExtraData(true, selectedTime);

                const order: any = {
                    table: isTakeAway ? null : tableId,
                    session: sessionId || undefined,
                    client_identifier: isTakeAway ? sessionId || identifier : identifier,
                    scheduled_at: new Date(scheduledOrder.scheduledDate + 'T' + selectedTime + ':00').toISOString(),
                    order_products: scheduledOrder.items.map(item => ({
                        product: item.product.uuid,
                        quantity: item.quantity,
                        observation: item.observation,
                        sub_products: item.sub_products?.map(s => s.sub_product_uuid) || []
                    })),
                    order_offers: scheduledOrder.offers.map(item => ({
                        offer: item.offer.uuid,
                        quantity: item.quantity,
                        observation: item.observation
                    })),
                    observation: scheduledOrder.notes || notes || undefined,
                    ...scheduledExtraData,
                    latitude: data.address?.latitude,
                    longitude: data.address?.longitude,
                    idempotency_key: `order-scheduled-${sessionId}-${scheduledOrder.scheduledDate}-${Date.now()}`
                };
                ordersToSubmit.push(order);
            }

            console.log('[DEBUG ORDER SUBMIT] Órdenes finalizadas a enviar:', JSON.stringify(ordersToSubmit));
            // Siempre redirigir al pago — la orden se crea en PaymentPage
            const wasScheduled = hasScheduledOrders();

            navigate(`/payment/${routeId || tableId}`, {
                state: {
                    paymentType: 'individual',
                    pendingOrdersToSubmit: ordersToSubmit,
                    wasScheduled,
                    totalAmount: grandTotal + (deliveryQuote ? parseFloat(deliveryQuote.price) : 0)
                }
            });

        } catch (e) {
            console.error(e);
            alert('Error al crear la orden. Intenta nuevamente.');
        }
    };

    const renderInput = (
        label: string,
        registerName: any,
        placeholder: string,
        icon?: React.ReactNode,
        required = true,
        fullWidth = true
    ) => (
        <div className={`mb-4 ${fullWidth ? 'w-full' : 'flex-1'}`}>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                {label} {required && <span className="text-red-500">*</span>}
            </label>
            <div className="relative">
                {icon && (
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                        {icon}
                    </div>
                )}
                <input
                    {...register(registerName)}
                    placeholder={placeholder}
                    className={`w-full ${icon ? 'pl-10' : 'pl-4'} pr-4 py-3 bg-white dark:bg-gray-800 border ${
                        // @ts-expect-error - dynamic access
                        errors[registerName] ? 'border-red-500' : 'border-gray-200 dark:border-gray-700'
                        } rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 shadow-sm`}
                />
                {/* @ts-expect-error - dynamic access */}
                {errors[registerName] && (
                    <p className="text-red-500 text-xs mt-1">
                        {/* @ts-expect-error - dynamic access */}
                        {errors[registerName]?.message as string}
                    </p>
                )}
            </div>
        </div>
    );

    const isProcessing = createOrderMutation.isPending || createBatchOrdersMutation.isPending;

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-32">
            <Header title="Finalizar Pedido" showBack />

            <div className="max-w-xl mx-auto p-6 space-y-6">

                {/* --- DATOS DE ENTREGA / RETIRO --- */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            {orderType === 'delivery' ? <MapPin className="text-primary w-5 h-5" /> : <ShoppingBag className="text-primary w-5 h-5" />}
                        </div>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                            {orderType === 'delivery' ? 'Datos de Envío' : orderType === 'pickup' ? 'Datos de Retiro' : 'Información'}
                        </h2>
                    </div>

                    {orderType === 'pickup' && (
                        <div className="space-y-2">
                            {renderInput("Nombre de quien retira", "pickup_name", "Juan Pérez", <User className="w-4 h-4" />)}
                            {renderInput("Teléfono de contacto", "pickup_phone", "+54 9 11...", <Phone className="w-4 h-4" />)}
                        </div>
                    )}

                    {orderType === 'delivery' && (
                        <div className="space-y-4">
                            {/* Login Prompt for Addresses */}
                            {!isAuthenticated && (
                                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl mb-4 text-center">
                                    <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">Inicia sesión con Google para usar tus direcciones guardadas</p>
                                    <div className="flex justify-center">
                                        <GoogleLogin
                                            onSuccess={handleGoogleLogin}
                                            onError={() => console.log('Login Failed')}
                                            useOneTap
                                            shape="pill"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Address Selection */}
                            {isAuthenticated && (
                                <div className="mb-6">
                                    <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Dirección de entrega</h3>

                                    <div className="bg-gray-50 dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 p-4">
                                        <AddressList
                                            selectable
                                            selectedId={selectedAddressUuid}
                                            onSelect={handleSelectAddress}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Hidden inputs to maintain form state compatibility */}
                            <input type="hidden" {...register('address_uuid')} />
                            <input type="hidden" {...register('address.street')} />
                            <input type="hidden" {...register('address.number')} />
                            <input type="hidden" {...register('address.floor')} />
                            <input type="hidden" {...register('address.apartment')} />
                            <input type="hidden" {...register('address.district')} />
                            <input type="hidden" {...register('address.reference')} />
                            <input type="hidden" {...register('address.latitude', { valueAsNumber: true })} />
                            <input type="hidden" {...register('address.longitude', { valueAsNumber: true })} />

                            {errors.address && (
                                <div className="p-3 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-sm rounded-xl mb-4 border border-red-200 dark:border-red-800">
                                    Por favor selecciona una dirección de envío válida.
                                </div>
                            )}

                            <div className="space-y-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                                <h3 className="font-semibold text-gray-900 dark:text-white">Datos de quien recibe</h3>
                                {renderInput("Nombre de quien recibe", "recipient_name", "Juan Pérez", <User className="w-4 h-4" />)}
                                {renderInput("Teléfono de contacto", "recipient_phone", "+54 9 11...", <Phone className="w-4 h-4" />)}
                            </div>
                        </div>
                    )}

                    {orderType === 'dine_in' && (
                        <p className="text-gray-500 text-sm">Pedido para la mesa {tableId}</p>
                    )}
                </div>

                {/* --- DATOS PARA PEDIDOS PROGRAMADOS --- */}
                {hasScheduledOrders() && (
                    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-2xl p-6 shadow-sm border border-indigo-200 dark:border-indigo-800">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center">
                                <Calendar className="text-indigo-600 dark:text-indigo-400 w-5 h-5" />
                            </div>
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                                Pedidos Programados
                            </h2>
                        </div>

                        {/* Email de contacto */}
                        <div className="mb-6">
                            {renderInput("Email de contacto", "contact_email", "tu@email.com", <User className="w-4 h-4" />)}
                            <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-2 flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" />
                                Se te recordará 24hs antes de cada pedido
                            </p>
                        </div>

                        {/* Horarios por cada orden programada */}
                        <div className="space-y-4">
                            <h3 className="font-semibold text-gray-900 dark:text-white text-sm mb-3">Seleccioná el horario de cada pedido</h3>
                            {scheduledOrders.map(order => {
                                const availableSlots = getAvailableTimeSlots(order.scheduledDate, config);

                                return (
                                    <div key={order.scheduledDate} className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-indigo-100 dark:border-indigo-800">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center justify-between">
                                            <span>{formatDate(order.scheduledDate)}</span>
                                            {availableSlots.length === 0 && (
                                                <span className="text-xs text-red-500 font-normal">Cerrado este día</span>
                                            )}
                                        </label>

                                        {availableSlots.length > 0 ? (
                                            <div className="relative">
                                                <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                                <select
                                                    {...register(`scheduled_times.${order.scheduledDate}`)}
                                                    className="w-full pl-10 pr-4 py-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 appearance-none"
                                                >
                                                    <option value="">Seleccioná un horario</option>
                                                    {availableSlots.map(time => (
                                                        <option key={time} value={time}>{time}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        ) : (
                                            <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm rounded-lg border border-red-100 dark:border-red-800">
                                                El restaurante no abre este día. Por favor eliminá este pedido o elegí otra fecha.
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* --- RESUMEN DEL PEDIDO --- */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                            <CheckCircle className="text-green-500 w-5 h-5" />
                        </div>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                            Resumen del Pedido
                        </h2>
                    </div>

                    <div className="space-y-4">
                        {/* Immediate Order */}
                        {hasImmediateOrders() && (
                            <div className="border-b border-gray-100 dark:border-gray-700 pb-4">
                                <h3 className="font-semibold text-gray-700 dark:text-gray-300 mb-2 text-sm uppercase tracking-wide">Para Ahora</h3>
                                <div className="space-y-2">
                                    {items.map(item => (
                                        <div key={item.product.uuid} className="flex flex-col text-sm">
                                            <div className="flex justify-between">
                                                <span className="text-gray-600 dark:text-gray-400">{item.quantity}x {item.product.name}</span>
                                                <span className="font-medium text-gray-900 dark:text-white">
                                                    {currencySymbol}{((item.product.price + (item.sub_products?.reduce((acc, s) => acc + s.additional_price, 0) || 0)) * item.quantity).toFixed(2)}
                                                </span>
                                            </div>
                                            {item.sub_products && item.sub_products.length > 0 && (
                                                <div className="flex flex-wrap gap-1 ml-4 mt-1">
                                                    {item.sub_products.map(s => (
                                                        <span key={s.sub_product_uuid} className="text-[10px] font-bold text-primary bg-primary/5 px-1.5 py-0.5 rounded">
                                                            + {s.sub_product_name}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    {offers.map(item => (
                                        <div key={item.offer.uuid} className="flex justify-between text-sm">
                                            <span className="text-green-600 dark:text-green-400">{item.quantity}x {item.offer.name}</span>
                                            <span className="font-medium text-gray-900 dark:text-white">{currencySymbol}{(item.offer.price * item.quantity).toFixed(2)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Scheduled Orders */}
                        {hasScheduledOrders() && (
                            <div className="space-y-4">
                                {scheduledOrders.map(order => (
                                    <div key={order.scheduledDate} className="border-b border-gray-100 dark:border-gray-700 pb-4">
                                        <h3 className="font-semibold text-indigo-600 dark:text-indigo-400 mb-2 text-sm flex items-center gap-2">
                                            <Calendar className="w-3 h-3" /> {formatDate(order.scheduledDate)}
                                        </h3>
                                        <div className="space-y-2">
                                            {order.items.map(item => (
                                                <div key={item.product.uuid} className="flex flex-col text-sm">
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600 dark:text-gray-400">{item.quantity}x {item.product.name}</span>
                                                        <span className="font-medium text-gray-900 dark:text-white">
                                                            {currencySymbol}{((item.product.price + (item.sub_products?.reduce((acc, s) => acc + s.additional_price, 0) || 0)) * item.quantity).toFixed(2)}
                                                        </span>
                                                    </div>
                                                    {item.sub_products && item.sub_products.length > 0 && (
                                                        <div className="flex flex-wrap gap-1 ml-4 mt-1">
                                                            {item.sub_products.map(s => (
                                                                <span key={s.sub_product_uuid} className="text-[10px] font-bold text-primary bg-primary/5 px-1.5 py-0.5 rounded">
                                                                    + {s.sub_product_name}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                            {order.offers.map(item => (
                                                <div key={item.offer.uuid} className="flex justify-between text-sm">
                                                    <span className="text-green-600 dark:text-green-400">{item.quantity}x {item.offer.name}</span>
                                                    <span className="font-medium text-gray-900 dark:text-white">{currencySymbol}{(item.offer.price * item.quantity).toFixed(2)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="flex justify-between items-center pt-2">
                            <span className="font-bold text-lg text-gray-900 dark:text-white">Subtotal</span>
                            <span className="font-bold text-lg text-gray-900 dark:text-white">{currencySymbol}{grandTotal.toFixed(2)}</span>
                        </div>
                        {orderType === 'delivery' && (
                            <div className="flex justify-between items-center text-sm">
                                <span className="text-gray-600 dark:text-gray-400">Costo de envío</span>
                                <span className="font-medium text-gray-900 dark:text-white">
                                    {isGettingQuote ? 'Calculando...' : (deliveryQuote ? `${currencySymbol}${deliveryQuote.price}` : '—')}
                                </span>
                            </div>
                        )}
                        {orderType === 'delivery' && quoteError && (
                            <div className="flex justify-end items-center text-sm">
                                <span className="text-red-500 font-medium">{quoteError}</span>
                            </div>
                        )}
                        <div className="flex justify-between items-center pt-2 border-t border-gray-100 dark:border-gray-700">
                            <span className="font-bold text-xl text-gray-900 dark:text-white">Total</span>
                            <span className="font-bold text-2xl text-primary">
                                {currencySymbol}{(grandTotal + (deliveryQuote ? parseFloat(deliveryQuote.price) : 0)).toFixed(2)}
                            </span>
                        </div>
                    </div>
                </div>

            </div>

            {/* --- BOTONES FIJOS --- */}
            <div className="fixed bottom-0 left-0 right-0 p-4 bg-white dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] z-40">
                <div className="max-w-xl mx-auto flex gap-4">
                    <button
                        onClick={() => navigate(-1)}
                        type="button"
                        className="flex-1 py-4 rounded-xl font-bold bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                        Volver
                    </button>
                    <button
                        onClick={handleSubmit(onSubmit)}
                        disabled={isProcessing || isGettingQuote || (orderType === 'delivery' && !deliveryQuote)}
                        className="flex-[2] py-4 rounded-xl font-bold btn-primary text-white shadow-lg shadow-primary/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                        {isProcessing || isGettingQuote ? (
                            <>
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                {isGettingQuote ? 'Cotizando envío...' : 'Procesando...'}
                            </>
                        ) : (
                            <>
                                {((orderType === 'delivery' || orderType === 'pickup') && (hasImmediateOrders() || hasScheduledOrders())) ? 'Pagar' : 'Confirmar Pedido'}
                            </>
                        )}
                    </button>
                </div>
            </div>

            <AddressModal
                isOpen={isAddressModalOpen}
                onClose={() => {
                    setIsAddressModalOpen(false);
                    setAddressToEdit(undefined);
                }}
                addressToEdit={addressToEdit}
            />
        </div>
    );
}
